import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Modal, Tabs, Tab, Spinner, ListGroup, Badge, Alert } from 'react-bootstrap';
import { FaHistory, FaEye, FaTimes, FaInfoCircle, FaDownload } from 'react-icons/fa';
import { toast } from 'react-toastify';
import DOMPurify from 'dompurify';
import dayjs from 'dayjs';
import './TermsHistory.css';
import { getAcceptanceHistory } from 'Services/TermsService';
import { getMediaUrl } from 'Utils/Helpers';

const TermsHistory = () => {
    const [history, setHistory] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedVersion, setSelectedVersion] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [activeTab, setActiveTab] = useState(0);

    useEffect(() => {
        loadHistory();
    }, []);

    const loadHistory = async () => {
        try {
            const response = await getAcceptanceHistory();
            setHistory(response.data);
        } catch (err) {
            console.error('Échec du chargement de l’historique des acceptations', err);
            toast.error('Échec du chargement de l’historique des acceptations');
        } finally {
            setLoading(false);
        }
    };

    const openVersion = (version) => {
        setSelectedVersion(version);
        setActiveTab(0);
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setSelectedVersion(null);
    };

    const sanitizeHTML = (html) => {
        return { __html: DOMPurify.sanitize(html) };
    };

    if (loading) {
        return (
            <div className="py-5">
                <div className="text-center">
                    <Spinner animation="border" variant="primary" />
                    <p className="mt-3 text-muted">Chargement de votre historique d’acceptation...</p>
                </div>
            </div>
        );
    }

    return (
        <div className="terms-history-container py-4">
            <Row className="mb-4">
                <Col>
                    <h1>
                        <FaHistory className="me-2" />
                        Historique des Conditions Générales d’Utilisation
                    </h1>
                    <p className="text-muted">
                        Consultez toutes les versions des Conditions Générales d’Utilisation que vous avez acceptées
                    </p>
                </Col>
            </Row>

            <Container className="terms-history-container py-4">
                {history.length === 0 ? (
                    <Row>
                        <Col>
                            <Card className="text-center py-5">
                                <Card.Body>
                                    <FaHistory size={60} className="text-muted mb-3" />
                                    <h4 className="text-muted">Aucun historique d’acceptation pour le moment.</h4>
                                    <p className="text-muted">
                                        Vous n’avez encore accepté aucune Condition Générale d’Utilisation.
                                    </p>
                                </Card.Body>
                            </Card>
                        </Col>
                    </Row>
                ) : (
                    <Row>
                        {history.map((item) => (
                            <Col key={item.id} lg={6} className="mb-4">
                                <Card className="h-100 shadow-sm history-card">
                                    <Card.Header className="bg-primary text-white">
                                        <div className="d-flex justify-content-between align-items-center">
                                            <h5 className="mb-0">Version {item.version}</h5>
                                            <Badge bg="light" text="dark">
                                                {item.tabs.length} {item.tabs.length === 1 ? 'Section' : 'Sections'}
                                            </Badge>
                                        </div>
                                    </Card.Header>
                                    <Card.Body>
                                        <div className="mb-3">
                                            <small className="text-muted d-block">
                                                <strong>Acceptée le :</strong>{' '}
                                                {dayjs(item.acceptedDate).format('D MMMM YYYY [à] H:mm')}
                                            </small>
                                        </div>

                                        <div className="mb-3">
                                            <strong className="d-block mb-2">Sections :</strong>
                                            <ListGroup variant="flush" className="small">
                                                {item.tabs.map((tab, idx) => (
                                                    <ListGroup.Item key={idx} className="px-0 py-1">
                                                        • {tab.title}
                                                    </ListGroup.Item>
                                                ))}
                                            </ListGroup>
                                        </div>
                                    </Card.Body>
                                    <Card.Footer className="bg-light d-flex gap-2">
                                        <Button
                                            variant="primary"
                                            size="sm"
                                            className="flex-grow-1"
                                            onClick={() => openVersion(item)}
                                        >
                                            <FaEye className="me-2" />
                                            Voir les détails
                                        </Button>
                                        {item.fileUrl && (
                                            <a
                                                href={getMediaUrl(item.fileUrl)}
                                                download
                                                target="_blank"
                                                rel="noreferrer"
                                                className="btn btn-sm btn-outline-secondary"
                                                title="Télécharger le document"
                                            >
                                                <FaDownload />
                                            </a>
                                        )}
                                    </Card.Footer>
                                </Card>
                            </Col>
                        ))}
                    </Row>
                )}
            </Container>

            {/* Modal de visualisation des détails */}
            <Modal show={showModal} onHide={closeModal} size="lg" centered className="terms-history-modal">
                {selectedVersion && (
                    <>
                        <Modal.Header closeButton className="bg-primary text-white">
                            <Modal.Title className="w-100">
                                <div className="d-flex justify-content-between align-items-center">
                                    <span>Version {selectedVersion.version}</span>
                                    <Badge bg="light" text="dark" pill>
                                        {selectedVersion.tabs.length}{' '}
                                        {selectedVersion.tabs.length === 1 ? 'Section' : 'Sections'}
                                    </Badge>
                                </div>
                                <small className="d-block mt-2" style={{ fontSize: '0.85rem', opacity: 0.9 }}>
                                    Acceptée le : {dayjs(selectedVersion.acceptedDate).format('D MMMM YYYY [à] H:mm')}
                                </small>

                                {selectedVersion.changeDescription && (
                                    <Alert variant="info" className="mb-0 rounded-0 d-flex align-items-start">
                                        <FaInfoCircle className="me-2 mt-1" />
                                        &nbsp;
                                        <div>
                                            <strong>Nouveautés :</strong>
                                            <div className="mt-1">{selectedVersion.changeDescription}</div>
                                        </div>
                                    </Alert>
                                )}
                            </Modal.Title>
                        </Modal.Header>

                        <Modal.Body className="p-0">
                            {selectedVersion.tabs.length > 1 ? (
                                <Tabs
                                    activeKey={activeTab}
                                    onSelect={(k) => setActiveTab(parseInt(k))}
                                    className="px-3 pt-3"
                                >
                                    {selectedVersion.tabs.map((tab, index) => (
                                        <Tab eventKey={index} title={tab.title} key={index}>
                                            <div className="p-4 modal-content-area">
                                                <div
                                                    className="terms-html-content"
                                                    dangerouslySetInnerHTML={sanitizeHTML(tab.content)}
                                                />
                                            </div>
                                        </Tab>
                                    ))}
                                </Tabs>
                            ) : (
                                <div className="p-4">
                                    {selectedVersion.tabs[0] && (
                                        <>
                                            <h3>{selectedVersion.tabs[0].title}</h3>
                                            <div
                                                className="terms-html-content"
                                                dangerouslySetInnerHTML={sanitizeHTML(selectedVersion.tabs[0].content)}
                                            />
                                        </>
                                    )}
                                </div>
                            )}
                        </Modal.Body>

                        <Modal.Footer>
                            {selectedVersion.fileUrl && (
                                <a
                                    href={getMediaUrl(selectedVersion.fileUrl)}
                                    download
                                    target="_blank"
                                    rel="noreferrer"
                                    className="btn btn-outline-secondary me-auto"
                                >
                                    <FaDownload className="me-2" />
                                    Télécharger le document
                                </a>
                            )}
                            <Button variant="secondary" onClick={closeModal}>
                                <FaTimes className="me-2" />
                                Fermer
                            </Button>
                        </Modal.Footer>
                    </>
                )}
            </Modal>
        </div>
    );
};

export default TermsHistory;
